import { CheckCircle2, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError, describeApiError } from '@/api/problemDetails'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui'
import { cn } from '@/lib/utils'

import {
  type HandoffPolicy,
  type ServiceWindow,
  TIME_ZONES,
  useHandoffPolicy,
  useUpdateHandoffPolicy,
  WEEK_DAYS,
  type WeekDay,
} from '../handoffPolicy'

const SAVED_FEEDBACK_MS = 4_000
const DEFAULT_WINDOW = { opens: '08:00', closes: '18:00' } as const

const fieldClass =
  'rounded-md border border-border-default bg-background-elevated px-3 text-sm text-foreground-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-60'

interface Draft {
  readonly businessHoursEnabled: boolean
  readonly timeZoneId: string
  readonly schedule: readonly ServiceWindow[]
  readonly availableMessage: string
  readonly unavailableMessage: string
  readonly notifyTeamByEmail: boolean
}

function toDraft(policy: HandoffPolicy): Draft {
  return {
    businessHoursEnabled: policy.businessHoursEnabled,
    timeZoneId: policy.timeZoneId,
    schedule: policy.schedule,
    availableMessage: policy.availableMessage ?? '',
    unavailableMessage: policy.unavailableMessage ?? '',
    notifyTeamByEmail: policy.notifyTeamByEmail,
  }
}

/** Janelas inválidas (fecha antes de abrir) ou sobrepostas no mesmo dia — validado aqui e no servidor. */
function scheduleProblem(schedule: readonly ServiceWindow[]): 'inverted' | 'overlap' | null {
  if (schedule.some((window) => window.closes <= window.opens)) {
    return 'inverted'
  }

  for (const day of WEEK_DAYS) {
    const windows = schedule
      .filter((window) => window.day === day)
      .sort((a, b) => a.opens.localeCompare(b.opens))
    for (let index = 1; index < windows.length; index++) {
      const current = windows[index]
      const previous = windows[index - 1]
      if (current !== undefined && previous !== undefined && current.opens < previous.closes) {
        return 'overlap'
      }
    }
  }

  return null
}

/**
 * Atendimento humano (ADR-027): quando a equipe atende (horário, opcional), o que o cliente lê na transferência (com
 * e sem equipe disponível) e o aviso por e-mail à equipe. Mostra o status real da equipe agora. Some para quem não vê
 * agentes; edição exige gerenciar agentes (o servidor valida).
 */
export function HandoffPolicyCard({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation()
  const policyQuery = useHandoffPolicy(tenantId)

  if (policyQuery.error instanceof ApiError && policyQuery.error.problem.status === 403) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.handoff.title')}</CardTitle>
        <CardDescription>{t('settings.handoff.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {policyQuery.data === undefined ? (
          policyQuery.isError ? (
            <Alert variant="danger" title={t('settings.handoff.loadFailed')} />
          ) : (
            <Spinner label={t('loading')} />
          )
        ) : (
          <HandoffPolicyForm tenantId={tenantId} policy={policyQuery.data} />
        )}
      </CardContent>
    </Card>
  )
}

/** Formulário da política; o rascunho nasce da política carregada (sem sincronizar estado em efeito). */
function HandoffPolicyForm({
  tenantId,
  policy,
}: {
  readonly tenantId: string
  readonly policy: HandoffPolicy
}) {
  const { t } = useTranslation()
  const timeZoneLabels: Readonly<Record<string, string>> = {
    'America/Sao_Paulo': t('settings.handoff.timeZones.America_Sao_Paulo'),
    'America/Bahia': t('settings.handoff.timeZones.America_Bahia'),
    'America/Fortaleza': t('settings.handoff.timeZones.America_Fortaleza'),
    'America/Recife': t('settings.handoff.timeZones.America_Recife'),
    'America/Belem': t('settings.handoff.timeZones.America_Belem'),
    'America/Manaus': t('settings.handoff.timeZones.America_Manaus'),
    'America/Cuiaba': t('settings.handoff.timeZones.America_Cuiaba'),
    'America/Campo_Grande': t('settings.handoff.timeZones.America_Campo_Grande'),
    'America/Porto_Velho': t('settings.handoff.timeZones.America_Porto_Velho'),
    'America/Boa_Vista': t('settings.handoff.timeZones.America_Boa_Vista'),
    'America/Rio_Branco': t('settings.handoff.timeZones.America_Rio_Branco'),
    'America/Noronha': t('settings.handoff.timeZones.America_Noronha'),
  }
  const mutation = useUpdateHandoffPolicy(tenantId)
  const [draft, setDraft] = useState<Draft>(() => toDraft(policy))
  const [showSaved, setShowSaved] = useState(false)

  useEffect(() => {
    if (!showSaved) {
      return
    }
    const timer = setTimeout(() => {
      setShowSaved(false)
    }, SAVED_FEEDBACK_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [showSaved])

  function update(patch: Partial<Draft>): void {
    setDraft((current) => ({ ...current, ...patch }))
    setShowSaved(false)
    if (mutation.isError) {
      mutation.reset()
    }
  }

  function setDayWindows(day: WeekDay, windows: readonly ServiceWindow[]): void {
    update({ schedule: [...draft.schedule.filter((window) => window.day !== day), ...windows] })
  }

  const problem = scheduleProblem(draft.schedule)
  const noWindows = draft.businessHoursEnabled && draft.schedule.length === 0

  function submit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    if (problem !== null || noWindows || mutation.isPending) {
      return
    }

    mutation.mutate(
      {
        businessHoursEnabled: draft.businessHoursEnabled,
        timeZoneId: draft.timeZoneId,
        schedule: draft.schedule,
        availableMessage: draft.availableMessage.trim() === '' ? null : draft.availableMessage,
        unavailableMessage:
          draft.unavailableMessage.trim() === '' ? null : draft.unavailableMessage,
        notifyTeamByEmail: draft.notifyTeamByEmail,
      },
      {
        onSuccess: (saved) => {
          setDraft(toDraft(saved))
          setShowSaved(true)
        },
      },
    )
  }

  return (
    <form
      onSubmit={(event) => {
        submit(event)
      }}
      className="flex flex-col gap-5"
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-foreground-muted">{t('settings.handoff.teamNow')}</span>
        <Badge variant={policy.team.state === 'available' ? 'success' : 'warning'}>
          {policy.team.state === 'available'
            ? t('presence.team.available', { count: policy.team.onlineOperators })
            : policy.team.state === 'outside_business_hours'
              ? t('presence.team.outsideHours', { when: policy.team.returnText })
              : t('presence.team.nobodyOnline')}
        </Badge>
      </div>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-sm font-semibold text-foreground-primary">
          {t('settings.handoff.hours.title')}
        </legend>
        <label className="flex items-start gap-2 text-sm text-foreground-secondary">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={draft.businessHoursEnabled}
            onChange={(event) => {
              const enabled = event.target.checked
              update({
                businessHoursEnabled: enabled,
                // Ligar sem nenhuma janela: começa com o horário comercial padrão (seg–sex 08–18).
                schedule:
                  enabled && draft.schedule.length === 0
                    ? WEEK_DAYS.slice(0, 5).map((day) => ({ day, ...DEFAULT_WINDOW }))
                    : draft.schedule,
              })
            }}
          />
          <span className="flex flex-col">
            <span>{t('settings.handoff.hours.enable')}</span>
            <span className="text-xs text-foreground-muted">
              {draft.businessHoursEnabled
                ? t('settings.handoff.hours.enabledHint')
                : t('settings.handoff.hours.disabledHint')}
            </span>
          </span>
        </label>

        {draft.businessHoursEnabled ? (
          <>
            <label className="flex flex-col gap-1.5 text-sm sm:max-w-xs">
              <span className="font-medium text-foreground-secondary">
                {t('settings.handoff.hours.timeZone')}
              </span>
              <select
                className={cn(fieldClass, 'h-10')}
                value={draft.timeZoneId}
                onChange={(event) => {
                  update({ timeZoneId: event.target.value })
                }}
              >
                {(TIME_ZONES as readonly string[]).includes(draft.timeZoneId) ? null : (
                  <option value={draft.timeZoneId}>{draft.timeZoneId}</option>
                )}
                {TIME_ZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {timeZoneLabels[zone] ?? zone}
                  </option>
                ))}
              </select>
            </label>

            <ul className="flex flex-col divide-y divide-border-default rounded-md border border-border-default">
              {WEEK_DAYS.map((day) => {
                const windows = draft.schedule
                  .filter((window) => window.day === day)
                  .slice()
                  .sort((a, b) => a.opens.localeCompare(b.opens))
                const open = windows.length > 0

                return (
                  <li key={day} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start">
                    <label className="flex w-40 shrink-0 items-center gap-2 text-sm text-foreground-primary">
                      <input
                        type="checkbox"
                        checked={open}
                        onChange={(event) => {
                          setDayWindows(
                            day,
                            event.target.checked ? [{ day, ...DEFAULT_WINDOW }] : [],
                          )
                        }}
                      />
                      {t(`settings.handoff.days.${day}`)}
                    </label>
                    {open ? (
                      <div className="flex flex-1 flex-col gap-2">
                        {windows.map((window, index) => (
                          <div
                            key={`${day}-${String(index)}`}
                            className="flex flex-wrap items-center gap-2"
                          >
                            <input
                              type="time"
                              aria-label={t('settings.handoff.hours.opens', {
                                day: t(`settings.handoff.days.${day}`),
                              })}
                              className={cn(fieldClass, 'h-9')}
                              value={window.opens}
                              onChange={(event) => {
                                setDayWindows(
                                  day,
                                  windows.map((current, i) =>
                                    i === index
                                      ? { ...current, opens: event.target.value }
                                      : current,
                                  ),
                                )
                              }}
                            />
                            <span className="text-xs text-foreground-muted">
                              {t('settings.handoff.hours.until')}
                            </span>
                            <input
                              type="time"
                              aria-label={t('settings.handoff.hours.closes', {
                                day: t(`settings.handoff.days.${day}`),
                              })}
                              className={cn(fieldClass, 'h-9')}
                              value={window.closes}
                              onChange={(event) => {
                                setDayWindows(
                                  day,
                                  windows.map((current, i) =>
                                    i === index
                                      ? { ...current, closes: event.target.value }
                                      : current,
                                  ),
                                )
                              }}
                            />
                            {windows.length > 1 ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-label={t('settings.handoff.hours.removeWindow')}
                                onClick={() => {
                                  setDayWindows(
                                    day,
                                    windows.filter((_, i) => i !== index),
                                  )
                                }}
                              >
                                <Trash2 aria-hidden="true" className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="inline-flex w-fit items-center gap-1 text-xs font-medium text-action-primary hover:underline"
                          onClick={() => {
                            const last = windows[windows.length - 1]
                            setDayWindows(day, [
                              ...windows,
                              {
                                day,
                                opens: last?.closes ?? DEFAULT_WINDOW.opens,
                                closes: '23:59',
                              },
                            ])
                          }}
                        >
                          <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                          {t('settings.handoff.hours.addWindow')}
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-foreground-muted">
                        {t('settings.handoff.hours.closed')}
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>

            {problem !== null || noWindows ? (
              <p role="alert" className="text-xs text-status-danger">
                {noWindows
                  ? t('settings.handoff.hours.noWindows')
                  : problem === 'inverted'
                    ? t('settings.handoff.hours.inverted')
                    : t('settings.handoff.hours.overlap')}
              </p>
            ) : null}
          </>
        ) : null}
      </fieldset>

      <fieldset className="flex flex-col gap-4 border-t border-border-default pt-5">
        <legend className="mb-1 text-sm font-semibold text-foreground-primary">
          {t('settings.handoff.messages.title')}
        </legend>
        <p className="text-xs text-foreground-muted">{t('settings.handoff.messages.hint')}</p>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-foreground-secondary">
            {t('settings.handoff.messages.available')}
          </span>
          <textarea
            rows={3}
            maxLength={1000}
            className={cn(fieldClass, 'py-2')}
            placeholder={policy.defaultAvailableMessage}
            value={draft.availableMessage}
            onChange={(event) => {
              update({ availableMessage: event.target.value })
            }}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-foreground-secondary">
            {t('settings.handoff.messages.unavailable')}
          </span>
          <textarea
            rows={3}
            maxLength={1000}
            className={cn(fieldClass, 'py-2')}
            placeholder={policy.defaultUnavailableMessage}
            value={draft.unavailableMessage}
            onChange={(event) => {
              update({ unavailableMessage: event.target.value })
            }}
          />
        </label>
      </fieldset>

      <label className="flex items-start gap-2 border-t border-border-default pt-5 text-sm text-foreground-secondary">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={draft.notifyTeamByEmail}
          onChange={(event) => {
            update({ notifyTeamByEmail: event.target.checked })
          }}
        />
        <span className="flex flex-col">
          <span>{t('settings.handoff.notify.label')}</span>
          <span className="text-xs text-foreground-muted">{t('settings.handoff.notify.hint')}</span>
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" loading={mutation.isPending} disabled={problem !== null || noWindows}>
          {t('settings.handoff.save')}
        </Button>
        {showSaved ? (
          <p role="status" className="flex items-center gap-1.5 text-sm text-status-success">
            <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
            {t('settings.handoff.saved')}
          </p>
        ) : null}
      </div>

      {mutation.isError ? (
        <Alert variant="danger" title={t('settings.handoff.saveFailed')}>
          {describeApiError(mutation.error).join(' ')}
        </Alert>
      ) : null}
    </form>
  )
}
